import React, { useState, useEffect, useCallback } from 'react';
import { jobTypesAPI, contractsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { fmt, symbolFor } from '../lib/currency';

export default function JobTypes() {
  const { isAdmin, user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const sym = symbolFor(orgCurrency);
  const [jobTypes, setJobTypes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [contractFilter, setContractFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJobType, setSelectedJobType] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_rate_payable: '',
    default_rate_chargeable: '',
    contract_id: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = contractFilter !== 'all' ? { contract_id: contractFilter } : {};
      const [jobTypesRes, contractsRes] = await Promise.all([
        jobTypesAPI.list(params),
        contractsAPI.list(),
      ]);
      setJobTypes(jobTypesRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to load job types');
    } finally {
      setLoading(false);
    }
  }, [contractFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!formData.contract_id) {
      toast.error('Please select a contract for this job type.');
      return;
    }
    try {
      await jobTypesAPI.create({
        ...formData,
        default_rate_payable: formData.default_rate_payable ? parseFloat(formData.default_rate_payable) : 0,
        default_rate_chargeable: formData.default_rate_chargeable ? parseFloat(formData.default_rate_chargeable) : 0,
        contract_id: formData.contract_id,
      });
      toast.success('Job type created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job type');
    }
  };

  const handleEdit = async () => {
    if (!selectedJobType) return;
    if (!formData.contract_id) {
      toast.error('Please select a contract for this job type.');
      return;
    }
    try {
      await jobTypesAPI.update(selectedJobType.id, {
        ...formData,
        default_rate_payable: formData.default_rate_payable ? parseFloat(formData.default_rate_payable) : 0,
        default_rate_chargeable: formData.default_rate_chargeable ? parseFloat(formData.default_rate_chargeable) : 0,
        contract_id: formData.contract_id,
      });
      toast.success('Job type updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job type');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this job type?')) return;
    try {
      await jobTypesAPI.delete(id);
      toast.success('Job type deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job type');
    }
  };

  const openEditModal = (jobType) => {
    setSelectedJobType(jobType);
    setFormData({
      name: jobType.name,
      description: jobType.description || '',
      default_rate_payable: jobType.default_rate_payable?.toString() || '',
      default_rate_chargeable: jobType.default_rate_chargeable?.toString() || '',
      contract_id: jobType.contract_id || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_rate_payable: '',
      default_rate_chargeable: '',
      contract_id: '',
    });
  };

  const filteredJobTypes = jobTypes.filter(jobType =>
    jobType.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (jobType.description && jobType.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="job-types-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-1 gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search job types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#2B2B2B] border-white/10 text-[#F2F2F2]"
              data-testid="search-job-types-input"
            />
          </div>
          <Select value={contractFilter} onValueChange={setContractFilter}>
            <SelectTrigger className="w-[180px] bg-[#2B2B2B] border-white/10" data-testid="contract-filter-select">
              <SelectValue placeholder="All Contracts" />
            </SelectTrigger>
            <SelectContent className="bg-[#2B2B2B] border-white/10">
              <SelectItem value="all">All Contracts</SelectItem>
              {contracts.map(contract => (
                <SelectItem key={contract.id} value={contract.id}>
                  {contract.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isAdmin() && (
          <Button
            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
            onClick={() => setShowCreateModal(true)}
            data-testid="add-job-type-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Job Type
          </Button>
        )}
      </div>

      {/* Job Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredJobTypes.length === 0 ? (
          <Card className="bg-[#2B2B2B] border-white/5 col-span-full">
            <CardContent className="p-8 text-center text-[#A1A1AA]">
              No job types found
            </CardContent>
          </Card>
        ) : (
          filteredJobTypes.map((jobType) => (
            <Card key={jobType.id} className="bg-[#2B2B2B] border-white/5 hover:border-[#1ABC9C]/30 transition-colors" data-testid={`job-type-card-${jobType.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FFC04D]/20 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-[#FFC04D]" />
                    </div>
                    <div>
                      <CardTitle className="text-[#F2F2F2] text-base">{jobType.name}</CardTitle>
                      {jobType.contract_name && (
                        <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C] border-0 text-xs mt-1">
                          {jobType.contract_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {isAdmin() && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-[#F2F2F2]">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                        <DropdownMenuItem onClick={() => openEditModal(jobType)} className="text-[#F2F2F2] focus:bg-white/10">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(jobType.id)} className="text-red-400 focus:bg-white/10">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {jobType.description && (
                  <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-2">{jobType.description}</p>
                )}
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/5">
                  <div className="p-2 bg-[#1A1A1A] rounded-lg">
                    <p className="text-xs text-[#A1A1AA]">Rate Payable</p>
                    <p className="text-lg font-semibold text-[#1ABC9C]">
                      {fmt(jobType.default_rate_payable, orgCurrency)}
                    </p>
                  </div>
                  <div className="p-2 bg-[#1A1A1A] rounded-lg">
                    <p className="text-xs text-[#A1A1AA]">Rate Chargeable</p>
                    <p className="text-lg font-semibold text-[#F2F2F2]">
                      {fmt(jobType.default_rate_chargeable, orgCurrency)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Job Type Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Add New Job Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Job Type Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Event First Aid, Sports Cover"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="job-type-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the job type..."
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>
                Link to Contract <span className="text-red-400">*</span>
              </Label>
              <Select
                value={formData.contract_id || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, contract_id: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="jobtype-contract-select">
                  <SelectValue placeholder="Select a contract" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {contracts.map(contract => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Rate Payable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_payable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_payable: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Rate Chargeable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_chargeable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_chargeable: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-job-type-btn">
              Create Job Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Type Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Edit Job Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Job Type Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Link to Contract</Label>
              <Select 
                value={formData.contract_id || "none"} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, contract_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Select a contract" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="none">No Contract</SelectItem>
                  {contracts.map(contract => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Rate Payable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_payable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_payable: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Rate Chargeable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_chargeable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_chargeable: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleEdit} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
